import { useMemo, useState } from "react";

import type { Deviation } from "../contract/types";
import { runNumberOf } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError } from "../app/useResource";
import { shortVehicleNumber, vehicleNumberOrUnidentified } from "../app/format";
import { useConfig, useNetworkId, useThresholds } from "../config/ConfigProvider";
import { useLines } from "../config/LinesProvider";
import { isInDeviationScope } from "../map/deviationScope";
import { useActivePostParam, usePost } from "../posts/PostProvider";
import { BunchingBadge, DeviationBadge, LineBadge, ReleveRiskBadge } from "./bits";
import { useDeviations } from "./hooks";

/**
 * Onglets du panneau (P2-10) — mêmes seuils dans les deux
 * (`thresholds.generalTabEarlySeconds` = −3 min / `generalTabLateSeconds` = +8 min,
 * affichage seul, P2-11) :
 *  - « Général » : tout le réseau, périmètre affiné par un sélecteur
 *    **Exploitant** local (via `lineGroups.exploitantId`). Indépendant du poste.
 *  - « Poste » : le poste choisi dans la barre de menu (données filtrées serveur
 *    par `?post=`), regroupées par ligne (P2-08).
 * `isInDeviationScope` (filtre selon la NATURE de l'écart, `map/deviationScope.ts`,
 * signalé par Claude-2 v0.217.0 — ni positions figées, ni « sans temps réel » :
 * ces états vivent dans le bandeau « Sources dégradées ») décide seul de ce qui
 * entre dans ce panneau : avance ≥ 3 min / retard ≥ 8 min pour `late`/`early`,
 * tout `bunching` ou `releve-risk` émis par le moteur (leurs seuils respectifs
 * n'ont pas de sens ici — intervalle entre deux véhicules pour l'un, relève/
 * pause déjà tranchée par le moteur pour l'autre — cf. les badges dédiés,
 * `BunchingBadge` / `ReleveRiskBadge`, demande utilisateur 2026-10-02).
 * L'onglet « Dépôt » est supprimé (D-007).
 */
type QueueTab = "general" | "poste";

/** Rang de tri : priorité haute d'abord. */
function priorityRank(p: Deviation["priority"]): number {
  return p === "high" ? 0 : 1;
}

/**
 * Panneau « Avance/Retard » (ex-« File d'écarts », F1.2, renommé P2-10). Déjà
 * trié et seuillé par le serveur ; le tri local ne fait que réordonner. Deux
 * écritures possibles et pas une de plus : acquittement unitaire et acquittement
 * global — marqueurs internes au poste.
 */
export function DeviationQueue({
  selectedJourneyId,
  onSelect,
  onCollapse,
  onSendToMap,
}: {
  selectedJourneyId: string | null;
  onSelect: (deviation: Deviation) => void;
  onCollapse?: () => void;
  /** Carte pilote : bascule le panneau en pop-in sur la carte (bas-gauche). */
  onSendToMap?: () => void;
}) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const lines = useLines();
  const thresholds = useThresholds();
  const config = useConfig();
  const { activePost } = usePost();

  const [tab, setTab] = useState<QueueTab>("general");
  const [lineFilter, setLineFilter] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [busy, setBusy] = useState(false);

  // « Général » = tout le réseau (périmètre affiné par l'exploitant choisi ici) ;
  // « Poste » = le poste sélectionné dans la barre de menu. Les deux appliquent
  // les mêmes seuils (avance ≥ 3 min, retard ≥ 8 min).
  const postParam = useActivePostParam();
  const { items, loading, error, refresh } = useDeviations(tab === "poste" ? postParam : undefined);

  // Onglet Général = toutes les lignes de l'EXPLOITANT PAR DÉFAUT
  // (`config.defaultExploitantId`, décision utilisateur — plus de sélecteur
  // manuel « Tous les exploitants »), via `lineGroups.exploitantId`.
  const exploitantLineIds = useMemo(() => {
    if (!config.defaultExploitantId) return null;
    return new Set(
      config.lineGroups.filter((g) => g.exploitantId === config.defaultExploitantId).flatMap((g) => g.lineIds),
    );
  }, [config.defaultExploitantId, config.lineGroups]);
  const defaultExploitantLabel = config.exploitants.find((e) => e.id === config.defaultExploitantId)?.label ?? null;

  const lineOptions = useMemo(
    () => [...new Set(items.map((d) => d.lineId))].sort((a, b) => lines.code(a).localeCompare(lines.code(b))),
    [items, lines],
  );

  const rows = useMemo(() => {
    let list = items;
    if (tab === "general" && exploitantLineIds) list = list.filter((d) => exploitantLineIds.has(d.lineId));
    if (lineFilter) list = list.filter((d) => d.lineId === lineFilter);
    // §F : filtre selon la NATURE de l'écart (`kind`), pas seulement sa magnitude
    // — un `bunching` porte un INTERVALLE dans `deviationSeconds`, pas un écart à
    // l'horaire ; comparé aux seuils retard/avance, les 3 bunchings réels restaient
    // invisibles (intervalle sous le seuil) pendant qu'un intervalle large sur une
    // ligne peu fréquente se serait affiché comme un faux retard (bug signalé par
    // l'utilisateur, 2026-10-02 — voir `isInDeviationScope`).
    list = list.filter((d) => isInDeviationScope(d, thresholds));
    list = [...list].sort((a, b) => {
      if (tab === "poste") {
        return lines.code(a.lineId).localeCompare(lines.code(b.lineId)) || priorityRank(a.priority) - priorityRank(b.priority);
      }
      return (
        priorityRank(a.priority) - priorityRank(b.priority) ||
        Math.abs(b.deviationSeconds) - Math.abs(a.deviationSeconds)
      );
    });
    return list;
  }, [items, lineFilter, exploitantLineIds, tab, lines, thresholds]);

  // Badge d'en-tête : nombre de véhicules concernés **dans l'onglet affiché**
  // (après périmètre + seuils), non acquittés — pas le total réseau.
  const shownCount = rows.filter((d) => !d.ack.acked).length;
  // Acquittement de masse : tout ce qui est non acquitté dans le périmètre chargé.
  const openCount = items.filter((d) => !d.ack.acked).length;

  async function ack(request: Parameters<typeof backend.api.acknowledgeDeviations>[1]) {
    setBusy(true);
    try {
      await backend.api.acknowledgeDeviations(networkId, request);
      refresh();
    } catch (err) {
      console.error("Acquittement impossible", err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px 8px" }}>
        {onCollapse ? (
          <button
            type="button"
            className="button is-small is-white"
            aria-label="Réduire le panneau Avance/Retard"
            title="Réduire ce panneau"
            onClick={onCollapse}
            style={{ height: 30, width: 30, padding: 0, flex: "none", lineHeight: 1, fontSize: "1.2rem", fontWeight: 700 }}
          >
            «
          </button>
        ) : null}
        <span
          style={{
            flex: 1,
            font: "700 12.5px/1 var(--bulma-family-secondary)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--sentinelle-ink)",
          }}
        >
          Avance/Retard
        </span>
        <span
          className="tag mono"
          style={{ fontWeight: 700, background: "var(--sentinelle-signal)", color: "#fff" }}
          title={`${shownCount} véhicule(s) — onglet « ${tab === "poste" ? "Poste" : "Général"} »`}
        >
          {shownCount}
        </span>
        {onSendToMap ? (
          <button
            type="button"
            className="button is-small is-white"
            aria-label="Afficher Avance/Retard en pop-in sur la carte"
            title="Vers la carte (pop-in bas-gauche)"
            onClick={onSendToMap}
            style={{ height: 30, width: 30, padding: 0, flex: "none", lineHeight: 1 }}
          >
            ⤵
          </button>
        ) : null}
      </div>

      <div className="tabs is-small is-boxed" style={{ margin: 0 }}>
        <ul>
          <li className={tab === "general" ? "is-active" : ""}>
            <a className="ovl" onClick={() => setTab("general")}>
              Général
            </a>
          </li>
          <li className={tab === "poste" ? "is-active" : ""}>
            <a className="ovl" onClick={() => setTab("poste")}>
              Poste
            </a>
          </li>
        </ul>
      </div>

      {tab === "general" && defaultExploitantLabel ? (
        <p className="ovl" style={{ padding: "6px 14px 2px", color: "var(--sentinelle-ink-3)" }}>
          {defaultExploitantLabel}
        </p>
      ) : null}

      {tab === "poste" && !activePost ? (
        <p className="has-text-grey is-size-7" style={{ padding: "6px 14px" }}>
          Aucun poste sélectionné — choisir un poste dans la barre de menu.
        </p>
      ) : null}

      {showFilters ? (
        <div style={{ padding: "8px 14px", borderBottom: "1px solid var(--sentinelle-rule)" }}>
          <div className="select is-small is-fullwidth">
            <label className="is-sr-only" htmlFor="dev-line-filter">
              Filtrer par ligne
            </label>
            <select id="dev-line-filter" value={lineFilter} onChange={(e) => setLineFilter(e.target.value)}>
              <option value="">Toutes les lignes</option>
              {lineOptions.map((id) => (
                <option key={id} value={id}>
                  {lines.code(id)}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      <div className="mae-scroll-y" style={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <p className="p-4 has-text-grey is-size-7">Chargement…</p>
        ) : error ? (
          <p className="p-4 has-text-danger is-size-7">{describeError(error)}</p>
        ) : rows.length === 0 ? (
          <p className="p-4 has-text-grey is-size-7">Rien à signaler.</p>
        ) : (
          rows.map((deviation) => (
            <DeviationRow
              key={deviation.id}
              deviation={deviation}
              selected={deviation.journeyId === selectedJourneyId}
              busy={busy}
              onSelect={() => onSelect(deviation)}
              canAck={Boolean(activePost)}
              onAck={() => activePost && ack({ ids: [deviation.id], post: activePost.id })}
            />
          ))
        )}
      </div>

      <div style={{ padding: "10px 12px", borderTop: "1px solid var(--sentinelle-rule)", display: "flex", gap: 7 }}>
        <button
          type="button"
          className="button is-small"
          disabled={busy || openCount === 0 || !activePost}
          aria-label="Tout acquitter"
          title={activePost ? "Tout acquitter" : "Sélectionner un poste pour acquitter en masse"}
          style={{
            height: 30,
            width: 30,
            padding: 0,
            flex: "none",
            fontSize: "0.85rem",
            fontWeight: 700,
            background: "var(--sentinelle-ink)",
            color: "#fff",
            borderColor: "var(--sentinelle-ink)",
          }}
          onClick={() => activePost && ack({ all: true, post: activePost.id })}
        >
          {/* double-check = acquitter TOUT (vs « ✓ » = acquitter un seul écart) */}
          ✓✓
        </button>
        <button
          type="button"
          className="button is-small"
          aria-label="Filtres"
          aria-pressed={showFilters}
          title="Filtres"
          style={{ height: 30, width: 30, padding: 0, flex: "none" }}
          onClick={() => setShowFilters((v) => !v)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3 5h18l-7 8v6l-4 2v-8z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function DeviationRow({
  deviation,
  selected,
  busy,
  canAck,
  onSelect,
  onAck,
}: {
  deviation: Deviation;
  selected: boolean;
  busy: boolean;
  canAck: boolean;
  onSelect: () => void;
  onAck: () => void;
}) {
  const acknowledged = deviation.ack.acked;
  const severe = deviation.priority === "high";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "34px 1fr auto",
        gap: 10,
        alignItems: "center",
        padding: "9px 14px",
        borderBottom: "1px solid var(--bulma-border-weak)",
        borderLeft: `3px solid ${selected ? "var(--sentinelle-rail)" : severe ? "var(--sentinelle-signal)" : "transparent"}`,
        background: selected ? "hsl(211,60%,96%)" : severe && !acknowledged ? "hsl(18,80%,97%)" : "transparent",
        opacity: acknowledged ? 0.55 : 1,
        cursor: "pointer",
      }}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <LineBadge lineId={deviation.lineId} />
      <span style={{ minWidth: 0 }}>
        {/* Numéro de sortie nu (ou « ? »), l'arrêt actuel juste à côté — plus le
            mot « Sortie » ni le `runId` complet (déjà porté par la ligne/le
            badge). Le n° de parc descend en 2ᵉ ligne. */}
        <span
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            fontSize: "11.5px",
            fontWeight: 600,
            color: "var(--sentinelle-ink)",
          }}
        >
          <span className="mono" style={{ flex: "none" }}>
            {runNumberOf(deviation.runId) ?? "?"}
          </span>
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: 500,
              color: "var(--sentinelle-ink-3)",
            }}
          >
            {deviation.atStopName ?? "—"}
          </span>
        </span>
        <span className="mono" style={{ display: "block", fontSize: "10.5px", color: "var(--sentinelle-ink-3)" }}>
          {vehicleNumberOrUnidentified(deviation.vehicleDisplayNumber)}
        </span>
      </span>
      <span style={{ textAlign: "right", display: "flex", alignItems: "center", gap: 8 }}>
        <span>
          {/* `bunching` : `deviationSeconds` est un INTERVALLE, jamais un écart à
              l'horaire — `BunchingBadge` dédié, pas `DeviationBadge` (qui le
              lirait « retard »/« avance »). `releve-risk` : garde son vrai écart
              à l'horaire, le badge dédié (marge avant la relève) s'ajoute. */}
          {deviation.kind === "bunching" ? (
            <BunchingBadge intervalSeconds={deviation.deviationSeconds} style={{ display: "block" }} />
          ) : deviation.kind === "releve-risk" && deviation.releveRisk ? (
            <ReleveRiskBadge releveRisk={deviation.releveRisk} style={{ display: "block" }} />
          ) : (
            <DeviationBadge seconds={deviation.deviationSeconds} style={{ display: "block" }} />
          )}
          {acknowledged ? (
            <span className="ovl" style={{ display: "block", fontSize: "0.56rem", marginTop: 2 }}>
              acquitté
            </span>
          ) : deviation.kind === "bunching" ? (
            <span className="ovl" style={{ display: "block", fontSize: "0.56rem", marginTop: 2 }}>
              paquet
            </span>
          ) : deviation.kind === "releve-risk" && deviation.releveRisk ? (
            <span className="ovl" style={{ display: "block", fontSize: "0.56rem", marginTop: 2 }}>
              {deviation.releveRisk.legalBreakAtRisk ? "pause" : "relève"}
            </span>
          ) : null}
        </span>
        {!acknowledged ? (
          <button
            type="button"
            className="button is-small is-white"
            disabled={busy || !canAck}
            title={canAck ? "Acquitter cet écart" : "Sélectionner un poste pour acquitter"}
            aria-label={`Acquitter l'écart ${deviation.lineCode} ${shortVehicleNumber(deviation.vehicleDisplayNumber)}`}
            style={{ height: 30, padding: "0 6px" }}
            onClick={(e) => {
              e.stopPropagation();
              onAck();
            }}
          >
            ✓
          </button>
        ) : null}
      </span>
    </div>
  );
}
